import { Link, useNavigate } from 'react-router-dom'

import PositionDoughnutChart from '@Features/position/PositionDoughnutChart'
import type { PositionStatus } from '@Store/types/dashboard'
import SpinLoader from '@Utils/loaders/spin'
import Mixpanel from '@Utils/mixpanel/Mixpanel'

import ReportDetail from './report-detail'

function PricePosition({
  position,
  isReportDetail,
  isCheap,
  categoryId,
  brandId,
  view,
}: {
  position: PositionStatus | undefined
  isReportDetail?: boolean
  isCheap?: any
  categoryId?: string
  brandId?: string
  view?: string
}): JSX.Element {
  const navigate = useNavigate()

  const sendEvent = (event: string): void => {
    Mixpanel.track(event, {
      page: view,
      section: 'Price Position Card',
    })
  }

  return (
    <>
      {isReportDetail ? (
        <ReportDetail
          position={position}
          isCheap={isCheap}
          categoryId={categoryId}
          brandId={brandId}
        />
      ) : (
        <div className="flex flex-col sm:col-span-3 border border-solid border-gray-200 rounded-lg shadow mb-16">
          <div className="flex flex-col border-b border-solid border-gray-200 container mx-auto p-6">
            <span className="pb-1 font-medium text-lg text-gray-900">
              Price Position
            </span>
            <span className="font-normal text-sm text-gray-500">
              See how your products are positioned in the market, across
              different prices.
            </span>
          </div>
          {position ? (
            <>
              <div className="flex flex-col lg:flex-row container mx-auto px-4 py-5 lg:p-6 border-b border-solid border-gray-200 items-center">
                <div className="flex lg:w-2/3 order-last lg:order-first">
                  <div className="grid grid-cols-2 xl:grid-cols-3 gap-6 container mx-auto py-8 xl:py-16">
                    <button
                      onClick={(): void => {
                        sendEvent('Highest price Clicked')
                        navigate(
                          '/products?page=1&limit=10&orderBy=name&orderDirection=asc&is_cheap=5',
                        )
                      }}
                      className="flex justify-start"
                    >
                      <div className="flex flex-col">
                        <div className="flex flex-row items-center">
                          <div className="flex">
                            <div className="w-2.5 h-2.5 bg-orange-400 rounded-full"></div>
                          </div>

                          <span className="pl-2 font-medium text-sm text-gray-500">
                            Highest price
                          </span>
                        </div>

                        <span className="pl-4 font-semibold text-xl text-gray-900 pt-1">
                          {position?.highest} products
                        </span>
                      </div>
                    </button>
                    <button
                      onClick={(): void => {
                        sendEvent('Cheaper than average Clicked')
                        navigate(
                          '/products?page=1&limit=10&orderBy=name&orderDirection=asc&is_cheap=2',
                        )
                      }}
                      className="flex justify-start"
                    >
                      <div className="flex flex-col items-start">
                        <div className="flex flex-row items-center">
                          <div className="flex">
                            <div className="w-2.5 h-2.5 bg-green-400 rounded-full"></div>
                          </div>

                          <span className="pl-2 font-medium text-sm text-gray-500">
                            Cheaper than average
                          </span>
                        </div>

                        <span className="pl-4 font-semibold text-xl text-gray-900 pt-1">
                          {position?.cheaper} products
                        </span>
                      </div>
                    </button>
                    <button
                      onClick={(): void => {
                        sendEvent('Average Clicked')
                        navigate(
                          '/products?page=1&limit=10&orderBy=name&orderDirection=asc&is_cheap=3',
                        )
                      }}
                      className="flex justify-start"
                    >
                      <div className="flex flex-col">
                        <div className="flex flex-row items-center">
                          <div className="flex">
                            <div className="w-2.5 h-2.5 bg-violet-400 rounded-full"></div>
                          </div>

                          <span className="pl-2 font-medium text-sm text-gray-500">
                            Average
                          </span>
                        </div>

                        <span className="pl-4 font-semibold text-xl text-gray-900 pt-1">
                          {position?.average} products
                        </span>
                      </div>
                    </button>
                    <button
                      onClick={(): void => {
                        sendEvent('Higher than average Clicked')
                        navigate(
                          '/products?page=1&limit=10&orderBy=name&orderDirection=asc&is_cheap=4',
                        )
                      }}
                      className="flex justify-start"
                    >
                      <div className="flex flex-col items-start">
                        <div className="flex flex-row items-center">
                          <div className="flex">
                            <div className="w-2.5 h-2.5 bg-yellow-400 rounded-full"></div>
                          </div>

                          <span className="pl-2 font-medium text-sm text-gray-500">
                            Higher than average
                          </span>
                        </div>

                        <span className="pl-4 font-semibold text-xl text-gray-900 pt-1">
                          {position?.higher} products
                        </span>
                      </div>
                    </button>
                    <button
                      onClick={(): void => {
                        sendEvent('Cheapest Clicked')
                        navigate(
                          '/products?page=1&limit=10&orderBy=name&orderDirection=asc&is_cheap=1',
                        )
                      }}
                      className="flex justify-start"
                    >
                      <div className="flex flex-col items-start">
                        <div className="flex flex-row items-center">
                          <div className="flex">
                            <div className="w-2.5 h-2.5 bg-cyan-400 rounded-full"></div>
                          </div>

                          <span className="pl-2 font-medium text-sm text-gray-500">
                            Cheapest
                          </span>
                        </div>

                        <span className="pl-4 font-semibold text-xl text-gray-900 pt-1">
                          {position?.cheapest} products
                        </span>
                      </div>
                    </button>
                    <button
                      onClick={(): void => {
                        sendEvent('All equal Clicked')
                        navigate(
                          '/products?page=1&limit=10&orderBy=name&orderDirection=asc&is_cheap=6',
                        )
                      }}
                      className="flex justify-start"
                    >
                      <div className="flex flex-col">
                        <div className="flex flex-row items-center">
                          <div className="flex">
                            <div className="w-2.5 h-2.5 bg-rose-400 rounded-full"></div>
                          </div>

                          <span className="pl-2 font-medium text-sm text-gray-500">
                            All equal
                          </span>
                        </div>

                        <span className="pl-4 font-semibold text-xl text-gray-900 pt-1">
                          {position?.allequal} products
                        </span>
                      </div>
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-center lg:w-1/2">
                  <div className="flex items-center justify-center">
                    <PositionDoughnutChart position={position} />
                  </div>
                </div>
              </div>
              <div className="flex justify-end container mx-auto p-6">
                <Link to="/products">
                  <button type="button">
                    <span className="font-medium text-sm text-gray-500 p-2">
                      View all products
                    </span>
                  </button>
                </Link>
              </div>
            </>
          ) : (
            <div className="h-72 flex justify-center items-center">
              <div className="flex flex-col items-center">
                <SpinLoader />
                <span className="font-medium text-sm text-gray-700 pt-4">
                  Calculating...
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  )
}

export default PricePosition
